import React from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider, useCart } from "./context/CartContext";
import { Toaster } from "./components/ui/sonner";
import CartSheet from "./components/CartSheet";
import ItemCustomizeSheet from "./components/ItemCustomizeSheet";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Feed from "./pages/Feed";
import Search from "./pages/Search";
import Sell from "./pages/Sell";
import Orders from "./pages/Orders";
import Profile from "./pages/Profile";
import VendorDetail from "./pages/VendorDetail";
import Checkout from "./pages/Checkout";
import GoLive from "./pages/GoLive";
import WatchLive from "./pages/WatchLive";
import Messages from "./pages/Messages";
import AdminDashboard from "./pages/AdminDashboard";
import Community from "./pages/Community";
import CreatePost from "./pages/CreatePost";
import UserProfile from "./pages/UserProfile";
import Support from "./pages/Support";
import Live from "./pages/Live";
import VendorSubscription from "./pages/VendorSubscription";
import VendorShopEdit from "./pages/VendorShopEdit";
import VendorInsights from "./pages/VendorInsights";
import ProfileCustomize from "./pages/ProfileCustomize";
import TrashBin from "./pages/TrashBin";
import CreditStore from "./pages/CreditStore";
import WalletPage from "./pages/WalletPage";
import CashoutPage from "./pages/CashoutPage";
import AdminEconomy from "./pages/AdminEconomy";
import AdminCredentials from "./pages/AdminCredentials";
import MyMapPin from "./pages/MyMapPin";
import PublicStorefront from "./pages/PublicStorefront";
import VendorCredentials from "./pages/VendorCredentials";
import VendorTeam from "./pages/VendorTeam";
import VendorPromotions from "./pages/VendorPromotions";
import VendorLoyalty from "./pages/VendorLoyalty";
import VendorMenuBuilder from "./pages/VendorMenuBuilder";
import VendorOrderCenter from "./pages/VendorOrderCenter";
import VendorIssueInbox from "./pages/VendorIssueInbox";
import DriverView from "./pages/DriverView";
import Receipt from "./pages/Receipt";
import ShopLocations from "./pages/ShopLocations";
import MyLoyalty from "./pages/MyLoyalty";
import ShopInbox from "./pages/ShopInbox";
import AppShell from "./components/AppShell";
import GiftBurstProvider from "./components/GiftBurst";

import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-black">
        <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously route to AuthCallback when session_id is in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/shop/:shopId" element={<PublicStorefront />} />
      <Route path="/vendor/:vendorId" element={<VendorDetail />} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/feed" element={<Feed />} />
        <Route path="/search" element={<Search />} />
        <Route path="/sell" element={<Sell />} />
        <Route path="/sell/shop" element={<VendorShopEdit />} />
        <Route path="/shop/team" element={<VendorTeam />} />
        <Route path="/shop/credentials" element={<VendorCredentials />} />
        <Route path="/shop/promotions" element={<VendorPromotions />} />
        <Route path="/shop/loyalty" element={<VendorLoyalty />} />
        <Route path="/shop/menu-builder" element={<VendorMenuBuilder />} />
        <Route path="/shop/order-center" element={<VendorOrderCenter />} />
        <Route path="/shop/issues" element={<VendorIssueInbox />} />
        <Route path="/shop/locations" element={<ShopLocations />} />
        <Route path="/driver" element={<DriverView />} />
        <Route path="/orders/:orderId/receipt" element={<Receipt />} />
        <Route path="/me/loyalty" element={<MyLoyalty />} />
        <Route path="/shop/inbox" element={<ShopInbox />} />
        <Route path="/shop/map-pin" element={<MyMapPin />} />
        <Route path="/admin/credentials" element={<AdminCredentials />} />
        <Route path="/sell/insights" element={<VendorInsights />} />
        <Route path="/sell/upgrade" element={<VendorSubscription />} />
        <Route path="/sell/boost" element={<VendorSubscription />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/customize" element={<ProfileCustomize />} />
        <Route path="/profile/trash" element={<TrashBin />} />
        <Route path="/credits" element={<CreditStore />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/cashout" element={<CashoutPage />} />
        <Route path="/admin/economy" element={<AdminEconomy />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/golive" element={<GoLive />} />
        <Route path="/live/:streamId" element={<WatchLive />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:vendorId" element={<Messages />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/community" element={<Community />} />
        <Route path="/create-post" element={<CreatePost />} />
        <Route path="/u/:userId" element={<UserProfile />} />
        <Route path="/support" element={<Support />} />
        <Route path="/support/:ticketId" element={<Support />} />
        <Route path="/live" element={<Live />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <GiftBurstProvider>
              <ErrorBoundary>
                <AppRouter />
                <GlobalSheets />
                <Toaster theme="dark" position="top-center" />
              </ErrorBoundary>
            </GiftBurstProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Mounts <CartSheet /> and <ItemCustomizeSheet /> at the App root so BOTH
// public (Landing / Login / /shop /:shopId / /vendor/:vendorId) and
// protected (AppShell) routes can open them. Fixes the Phase-3 UX dead-end
// where a shared storefront link couldn't open the customize sheet.
function GlobalSheets() {
  const { user } = useAuth();
  const { customizeProductId, closeCustomize } = useCart();
  if (!user) return null; // Only authed users have a cart on the server.
  return (
    <>
      <CartSheet />
      <ItemCustomizeSheet productId={customizeProductId} onClose={closeCustomize} />
    </>
  );
}
